/**
 * The shapes of ZeymoAPI's /Messages endpoints (ZEY-2259), mirrored from the Application DTOs
 * (ConversationSummaryDto, ConversationMessageDto, ConversationPersonDto, ChatMessageEvent).
 */

export type ConversationKind = 'Direct' | 'Group' | 'Document';

/**
 * The records a document thread can hang off. The names are the API's ConversationDocumentType
 * names. PrintPlate is a plate in the print-plates addon (ZEY-2907); its uid is the plate's guid.
 */
export const MESSAGING_DOCUMENT_TYPES = ['SalesOrder', 'Customer', 'PurchaseOrder', 'SalesInvoice', 'PrintPlate'] as const;

export type MessagingDocumentType = (typeof MESSAGING_DOCUMENT_TYPES)[number];

export function isMessagingDocumentType(value: unknown): value is MessagingDocumentType {
  return typeof value === 'string' && (MESSAGING_DOCUMENT_TYPES as readonly string[]).includes(value);
}

export interface ConversationPerson {
  uid: string;
  name: string;
  email?: string;
}

export interface ConversationSummary {
  uid: string;
  kind: ConversationKind;
  /** The other person on a direct conversation, the group's name, or the document's label. */
  title: string;
  documentType?: MessagingDocumentType;
  documentUid?: string;
  lastMessageAt: string;
  lastMessagePreview?: string;
  lastMessageSenderName?: string;
  unreadCount: number;
  isMember: boolean;
  members: ConversationPerson[];
}

export interface ConversationMessage {
  id: number;
  uid: string;
  senderUid: string;
  senderName: string;
  body: string;
  sentAt: string;
  editedAt?: string;
  isDeleted: boolean;
  isMine: boolean;
}

export interface GetMyConversationsResult {
  conversations: ConversationSummary[];
  totalUnread: number;
}

export interface GetConversationMessagesResult {
  conversation: ConversationSummary;
  messages: ConversationMessage[];
  hasMore: boolean;
}

export interface ConversationRefResult {
  conversationUid: string;
}

export interface SentMessageResult {
  messageUid: string;
  messageId: number;
}

/** The `data` of a live `chat-message` notification. */
export interface ChatMessageEvent {
  conversationUid: string;
  conversationKind: ConversationKind;
  messageUid: string;
  messageId: number;
  kind: 'new' | 'edited' | 'deleted';
  senderUid: string;
  senderName: string;
  preview: string;
  title?: string;
  documentType?: MessagingDocumentType;
  documentUid?: string;
}

/** Where each document type's record opens, for a thread's link back to it. */
const DOCUMENT_VIEW_URLS: Record<MessagingDocumentType, (uid: string) => string> = {
  SalesOrder: (uid) => `/sales-orders/view?id=${encodeURIComponent(uid)}`,
  Customer: (uid) => `/customers/view?id=${encodeURIComponent(uid)}`,
  PurchaseOrder: (uid) => `/purchase-orders/view?id=${encodeURIComponent(uid)}`,
  SalesInvoice: (uid) => `/sales-invoices/view?id=${encodeURIComponent(uid)}`,
  // ca-printplates-ui's plate page (/plates/:guid), framed by the shell under /printplates.
  PrintPlate: (uid) => `/printplates/plates/${encodeURIComponent(uid)}`,
};

/** The shell URL of a thread's record, or null for a document type this shell does not know. */
export function documentViewUrl(documentType: string | null | undefined, documentUid: string | null | undefined): string | null {
  return isMessagingDocumentType(documentType) && documentUid ? DOCUMENT_VIEW_URLS[documentType](documentUid) : null;
}
